<template>
  <!-- Someone else's Pääsuvõtmed: a user's on their Kasutajad page (admin), a
       portal client's on the client page (admin, secretary). For a lost phone
       or someone leaving. Hidden while the group is off and nothing is stored. -->
  <section v-if="visible" class="pko" :class="{ 'pko--framed': framed }">
    <div class="pko-head">
      <h2>Pääsuvõtmed <small>· {{ keys.length }}</small></h2>
      <span v-if="!on" class="pko-off">välja lülitatud</span>
      <span v-else-if="required" class="pko-req">kohustuslik</span>
    </div>
    <p v-if="!keys.length" class="pko-empty">{{ kind === 'client' ? 'Klient pole pääsuvõtit lisanud.' : 'Kasutaja pole pääsuvõtit lisanud.' }}</p>
    <ul v-else class="pko-list">
      <li v-for="key in keys" :key="key.id" class="pko-row">
        <div class="pko-main">
          <b>{{ key.name }}</b>
          <small>Lisatud {{ day(key.created) }} · {{ key.used ? 'viimati ' + day(key.used) : 'pole kasutatud' }}</small>
        </div>
        <template v-if="confirming === key.id">
          <button type="button" class="pko-link pko-link--danger" :disabled="busy" @click="removeKeys(key.id)">Jah, kustuta</button>
          <button type="button" class="pko-link" @click="confirming = null">Ei</button>
        </template>
        <button v-else type="button" class="pko-link pko-link--danger" @click="confirming = key.id">Kustuta</button>
      </li>
    </ul>
    <div v-if="keys.length > 1" class="pko-foot">
      <template v-if="confirming === 'all'">
        <span>Kustutan kõik {{ keys.length }}?</span>
        <button type="button" class="pko-link pko-link--danger" :disabled="busy" @click="removeKeys(null)">Jah</button>
        <button type="button" class="pko-link" @click="confirming = null">Ei</button>
      </template>
      <button v-else type="button" class="pko-link pko-link--danger" @click="confirming = 'all'">Kustuta kõik pääsuvõtmed</button>
    </div>
    <p v-if="required && on" class="pko-hint">Pärast kustutamist logib {{ kind === 'client' ? 'klient lepingunumbri ja telefoniga' : 'kasutaja parooliga' }} sisse ja peab kohe uue võtme lisama.</p>
    <p v-if="error" class="pko-err" role="alert">{{ error }}</p>
  </section>
</template>

<script>
import api from '@/api'
import { day } from '@/plugins/passkey'

export default {
  props: {
    kind: { type: String, required: true },
    ownerId: { type: [Number, String], required: true },
    // A border and padding of its own, for a page that has no card to put it in.
    framed: { type: Boolean, default: false }
  },
  data () {
    return { keys: [], on: false, required: false, loaded: false, busy: false, confirming: null, error: '' }
  },
  computed: {
    visible () {
      return this.loaded && (this.on || this.keys.length > 0)
    }
  },
  watch: {
    ownerId: { handler: 'load', immediate: true }
  },
  methods: {
    day,
    async load () {
      if (!this.ownerId) return
      try {
        const data = await api.getPasskeysOf(this.kind, this.ownerId)
        this.keys = data.keys
        this.on = data.on
        this.required = data.required
      } catch (err) {
        // Not this person's to see (a role without access): nothing is drawn.
        this.keys = []
        this.on = false
      } finally {
        this.loaded = true
      }
    },
    async removeKeys (keyId) {
      this.busy = true
      this.error = ''
      try {
        this.keys = (await api.deletePasskeysOf(this.kind, this.ownerId, keyId)).keys
        this.confirming = null
      } catch (err) {
        this.error = err.message
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.pko { display: grid; gap: 10px; color: #16322A; }
.pko--framed { border: 1px solid #C9D2CE; border-radius: 8px; padding: 14px 18px; background: #fff; }
.pko-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; }
.pko-head h2 { margin: 0; font-size: 15px; font-weight: 700; }
.pko-head small { font-weight: 400; color: #5B6B66; }
.pko-off, .pko-req { font-size: 12px; font-weight: 600; padding: 2px 9px; border-radius: 999px; }
.pko-off { background: #E9EEEC; color: #3E504A; }
.pko-req { background: #EEF3FC; color: #2B3F66; }
.pko-empty { margin: 0; color: #5B6B66; font-size: 14px; }
.pko-list { list-style: none; margin: 0; padding: 0; }
.pko-row { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; padding: 8px 0; border-top: 1px solid #E1E7E4; }
.pko-row:first-child { border-top: 0; }
.pko-main { flex: 1 1 200px; min-width: 0; display: grid; }
.pko-main b { font-weight: 600; font-size: 15px; overflow-wrap: anywhere; }
.pko-main small { font-size: 13px; color: #3E504A; }
.pko-foot { display: flex; gap: 12px; align-items: center; flex-wrap: wrap; font-size: 14px; }
.pko-link { background: none; border: 0; padding: 4px 0; font: inherit; font-size: 14px; font-weight: 600; color: #3E504A; cursor: pointer; }
.pko-link--danger { color: #B3261E; }
.pko-link:hover { text-decoration: underline; }
.pko-link:disabled { opacity: .5; cursor: default; }
.pko-link:focus-visible { outline: 3px solid #1F5FD1; outline-offset: 2px; }
.pko-hint { margin: 0; font-size: 13px; color: #3E504A; }
.pko-err { margin: 0; padding: 8px 10px; border-radius: 6px; background: #FFF4E0; color: #5B3700; font-size: 14px; }
</style>
